import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useInvitationsApi } from '@app/hooks/useApis';
import { useAuthFlow } from '@app/context/auth-flow';
import { resolveErrorMessage } from '@app/utils/errors';
import { formatDateTime } from '@app/utils/format';
import type { InvitationAcceptResponse, ConnectionChoice } from '@app/types/sdk';
import type { DiscoveryMembership } from '@app/types/auth';

type Decision = 'accept' | 'reject';

type InviteContextClaims = Record<string, unknown> & {
  org_slug?: string;
  orgSlug?: string;
  org_name?: string;
  orgName?: string;
  org_id?: string;
  orgId?: string;
  roles?: unknown;
  role_slugs?: unknown;
  roleSlugs?: unknown;
  role_names?: unknown;
  roleNames?: unknown;
  email?: string;
  invitee_email?: string;
  user_email?: string;
  preferred_username?: string;
};

interface ParsedContextSuccess {
  ok: true;
  mode: 'detailed' | 'generic';
  claims: InviteContextClaims;
  organization: {
    slug?: string;
    name?: string;
    id?: string;
  };
  roles: string[];
  email?: string;
}

type ParsedContextResult = ParsedContextSuccess | { ok: false; error: string };

const EMAIL_KEYS: Array<keyof InviteContextClaims> = ['email', 'invitee_email', 'user_email', 'preferred_username'];

const decodeBase64Url = (value: string): string => {
  let normalized = value.replace(/-/g, '+').replace(/_/g, '/').trim();
  if (normalized.length === 0) {
    throw new Error('The context payload was empty.');
  }

  const padding = normalized.length % 4;
  if (padding) {
    normalized += '='.repeat(4 - padding);
  }

  let binary: string;
  if (typeof window !== 'undefined' && typeof window.atob === 'function') {
    binary = window.atob(normalized);
  } else if (typeof Buffer !== 'undefined') {
    binary = Buffer.from(normalized, 'base64').toString('binary');
  } else {
    throw new Error('Base64 decoding is not available in this environment.');
  }

  try {
    return decodeURIComponent(
      Array.prototype.map
        .call(binary, (char: string) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
  } catch {
    return binary;
  }
};

const extractString = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return undefined;
};

const extractStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (typeof entry === 'string') {
          return entry.trim();
        }
        if (entry && typeof entry === 'object') {
          const slug = extractString((entry as Record<string, unknown>).slug);
          if (slug) {
            return slug;
          }
          const name = extractString((entry as Record<string, unknown>).name);
          if (name) {
            return name;
          }
        }
        return null;
      })
      .filter((item): item is string => Boolean(item && item.length > 0));
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
  }

  return [];
};

const resolveEmailFromClaims = (claims: InviteContextClaims): string | undefined => {
  for (const key of EMAIL_KEYS) {
    const candidate = extractString(claims[key]);
    if (candidate) {
      return candidate;
    }
  }
  return undefined;
};

const resolveOrgSlug = (claims: InviteContextClaims, fallbackOrgId: string): string => {
  const direct = extractString(claims.org_slug ?? claims.orgSlug);
  if (direct) {
    return direct;
  }

  const name = extractString(claims.org_name ?? claims.orgName);
  if (name) {
    const normalized = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
    if (normalized.length > 0) {
      return normalized;
    }
  }

  return `org-${fallbackOrgId.slice(0, 8).toLowerCase()}`;
};

const parseInviteContext = (encoded: string | null): ParsedContextResult => {
  if (!encoded) {
    return {
      ok: true,
      mode: 'generic',
      claims: {},
      organization: {},
      roles: [],
      email: undefined
    };
  }

  try {
    const trimmed = encoded.trim();
    const appearsRawJwt = trimmed.includes('.') && /^[A-Za-z0-9._-]+$/.test(trimmed);

    const jwtToken = appearsRawJwt ? trimmed : decodeBase64Url(trimmed).trim();
    const segments = jwtToken.split('.');
    if (segments.length < 2) {
      throw new Error('Invitation context is malformed (missing JWT payload).');
    }

    const payloadJson = decodeBase64Url(segments[1]);
    const claims = JSON.parse(payloadJson) as InviteContextClaims;

    const organization = {
      slug: extractString(claims.org_slug ?? claims.orgSlug),
      name: extractString(claims.org_name ?? claims.orgName),
      id: extractString(claims.org_id ?? claims.orgId)
    };

    const roleSlugs = extractStringArray(claims.role_slugs ?? claims.roleSlugs);
    const roleNames = extractStringArray(claims.roles ?? claims.role_names ?? claims.roleNames);
    const roles = roleSlugs.length > 0 ? roleSlugs : roleNames;

    return {
      ok: true,
      mode: 'detailed',
      claims,
      organization,
      roles,
      email: resolveEmailFromClaims(claims)
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to interpret the invitation context.';
    return { ok: false, error: message };
  }
};

type MembershipConnection = {
  type?: string;
  name?: string;
  startUrl?: string | null;
  idpInitiated?: boolean | null;
};

type InvitationAcceptResponseExtended = InvitationAcceptResponse & {
  membership?: {
    orgId: string;
    orgSlug?: string | null;
    orgName?: string | null;
    status?: string | null;
    connections?: Array<MembershipConnection | null> | null;
    policy?: {
      requiresSso?: boolean | null;
    } | null;
  } | null;
  localConnections?: Array<'passkey' | 'password'> | null;
};

const mapConnections = (connections?: Array<MembershipConnection | null> | null): ConnectionChoice[] => {
  if (!connections || connections.length === 0) {
    return [];
  }

  const mapped: ConnectionChoice[] = [];
  connections.forEach((connection) => {
    if (!connection || !connection.type || !connection.name) {
      return;
    }
    const normalizedType: ConnectionChoice['type'] = connection.type === 'saml' ? 'saml' : 'oidc';
    mapped.push({
      type: normalizedType,
      name: connection.name,
      startUrl: connection.startUrl ?? undefined,
      idpInitiated: connection.idpInitiated ?? undefined
    });
  });
  return mapped;
};

const buildMembership = (claims: InviteContextClaims, response: InvitationAcceptResponseExtended): DiscoveryMembership => {
  if (response.membership) {
    const status = response.membership.status === 'active' ? 'active' : 'invited';
    const orgName =
      response.membership.orgName ?? resolveOrgSlug(claims, response.membership.orgId);
    const requiresSsoFlag = response.membership.policy?.requiresSso;
    const policy =
      typeof requiresSsoFlag === 'boolean' ? { requiresSso: requiresSsoFlag } : undefined;

    const membership: DiscoveryMembership = {
      orgId: response.membership.orgId,
      orgSlug: response.membership.orgSlug ?? resolveOrgSlug(claims, response.membership.orgId),
      orgName,
      status,
      connections: mapConnections(response.membership.connections),
      policy
    };

    if (status !== 'active') {
      membership.note = 'Complete the setup steps shown below to activate this membership.';
    }

    return membership;
  }

  const orgSlug = resolveOrgSlug(claims, response.orgId);
  const orgName =
    extractString(claims.org_name ?? claims.orgName) ??
    extractString(claims.org_slug ?? claims.orgSlug) ??
    response.orgId;

  const status = response.membershipStatus === 'active' ? 'active' : 'invited';

  const membership: DiscoveryMembership = {
    orgId: response.orgId,
    orgSlug,
    orgName,
    status,
    connections: []
  };

  if (status === 'invited') {
    membership.note = 'Finish the passkey step to activate this membership.';
  }

  return membership;
};

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const { inviteToken: inviteTokenParam } = useParams<{ inviteToken?: string }>();
  const inviteTokenQuery = searchParams.get('inviteToken');
  const inviteToken = useMemo(() => {
    const fromPath = inviteTokenParam?.trim();
    if (fromPath) {
      return fromPath;
    }

    const fromQuery = inviteTokenQuery?.trim();
    if (fromQuery) {
      return fromQuery;
    }

    return undefined;
  }, [inviteTokenParam, inviteTokenQuery]);
  const invitationsApi = useInvitationsApi();
  const navigate = useNavigate();
  const { setDiscoveryResult } = useAuthFlow();

  const contextParam = searchParams.get('context');
  const contextResult = useMemo(() => parseInviteContext(contextParam), [contextParam]);

  const [decision, setDecision] = useState<Decision>('accept');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [result, setResult] = useState<InvitationAcceptResponse | null>(null);
  const [flowPrimed, setFlowPrimed] = useState(false);
  const extendedResult = result as InvitationAcceptResponseExtended | null;

  const contextError = contextResult.ok ? null : contextResult.error;
  const isDetailed = contextResult.ok && contextResult.mode === 'detailed';

  useEffect(() => {
    setActionError(null);
  }, [contextParam, inviteToken]);

  useEffect(() => {
    if (!extendedResult || flowPrimed || !contextResult.ok || !contextResult.email) {
      return;
    }

    const current = extendedResult;
    const membership = buildMembership(contextResult.claims, current);

    const emailTxExpiresAt =
      current.emailTxExpiresAt instanceof Date ? current.emailTxExpiresAt.toISOString() : current.emailTxExpiresAt ?? null;

    setDiscoveryResult({
      email: contextResult.email,
      emailTx: current.emailTx,
      emailTxExpiresAt,
      localConnections: (current.localConnections ?? []) as Array<'passkey' | 'password'>,
      memberships: [membership]
    });

    setFlowPrimed(true);
  }, [contextResult, flowPrimed, result, setDiscoveryResult]);

  const organizationLabel = useMemo(() => {
    if (!isDetailed) {
      return null;
    }
    const fallback = contextResult.organization.id ?? null;
    return contextResult.organization.slug ?? contextResult.organization.name ?? fallback;
  }, [contextResult, isDetailed]);

  const rolesList = isDetailed ? contextResult.roles : [];
  const rolesLabel = rolesList.length > 0 ? rolesList.join(', ') : 'No roles assigned yet';

  const handleAccept = async () => {
    setDecision('accept');

    if (!inviteToken) {
      setActionError('This invitation is missing its token. Ask the sender to resend a fresh invitation link.');
      return;
    }

    if (busy || result) {
      return;
    }

    setBusy(true);
    setActionError(null);

    try {
      const response = await invitationsApi.authInvitationsAcceptPost({
        invitationAcceptRequest: { inviteToken }
      });
      setResult(response);
    } catch (caught) {
      const message = await resolveErrorMessage(
        caught,
        'We could not accept your invitation. Try again or request a new link.'
      );
      setActionError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleReject = () => {
    setDecision('reject');
    setActionError(null);
  };

  const goToNextStep = () => {
    if (!result) {
      return;
    }

    if (flowPrimed) {
      navigate('/orgs/select');
      return;
    }

    navigate('/login');
  };

  const acceptDisabled = Boolean(contextError) || busy || !inviteToken || Boolean(result);
  const rejectDisabled = busy || Boolean(result);
  const showRejectNotice = decision === 'reject' && !result;

  const localConnections: Array<'passkey' | 'password'> = extendedResult?.localConnections ?? [];
  const localConnectionLabels = localConnections.map((method) => (method === 'passkey' ? 'Passkey' : 'Password'));
  const membershipContext = extendedResult?.membership ?? null;
  const ssoConnections = mapConnections(membershipContext?.connections ?? null);
  const hasSsoConnections = ssoConnections.length > 0;
  const requiresSso = membershipContext?.policy?.requiresSso === true;

  const greetingName = isDetailed && contextResult.email ? ` ${contextResult.email}` : '';
  const membershipOrgLabel = membershipContext
    ? membershipContext.orgName ?? membershipContext.orgSlug ?? membershipContext.orgId
    : null;
  const organizationHighlight =
    membershipOrgLabel ??
    organizationLabel ??
    (isDetailed ? contextResult.organization.id ?? 'this organization' : 'this organization');

  return (
    <main className="auth-experience">
      <section className="auth-experience__card">
        <header className="auth-experience__header">
          <h1>Accept your invitation</h1>
          <p>Confirm the details below to continue.</p>
        </header>

        <div className="auth-experience__body auth-stack">
          {isDetailed ? (
            <p>
              Hi{greetingName}, you are invited to organization <strong>{organizationHighlight}</strong> and you are assigned roles{' '}
              <strong>{rolesLabel}</strong>. Would you like to accept the invite?
            </p>
          ) : (
            <p>
              You are about to accept an invitation to join an organization. Would you like to continue?
            </p>
          )}

          {isDetailed && (
            <section className="auth-section">
              <h2>Invitation details</h2>
              <p>
                <strong>Organization slug:</strong> {contextResult.organization.slug ?? '—'}
              </p>
              <p>
                <strong>Organization name:</strong> {contextResult.organization.name ?? '—'}
              </p>
              <p>
                <strong>Organization id:</strong> {contextResult.organization.id ?? result?.orgId ?? '—'}
              </p>
              <p>
                <strong>Roles:</strong> {rolesLabel}
              </p>
            </section>
          )}

          <div className="auth-inline-actions">
            <button
              type="button"
              className={decision === 'accept' ? 'auth-primary auth-primary--icon' : 'auth-secondary'}
              onClick={handleAccept}
              disabled={acceptDisabled}
            >
              {busy ? 'Accepting…' : result ? 'Invitation accepted' : 'Accept invite'}
            </button>
            <button
              type="button"
              className={decision === 'reject' ? 'auth-primary auth-primary--icon' : 'auth-secondary'}
              onClick={handleReject}
              disabled={rejectDisabled}
            >
              Reject invitation
            </button>
          </div>

          {contextError && (
            <p className="auth-error" role="alert">
              {contextError}
            </p>
          )}

          {actionError && (
            <p className="auth-error" role="alert">
              {actionError}
            </p>
          )}

          {showRejectNotice && (
            <p className="auth-note">
              We will leave the invitation untouched. Close this window or ask your administrator to revoke it when you&apos;re ready.
            </p>
          )}

          {result && (
            <section className="auth-section">
              <h2>Next steps</h2>
              <p>
                Membership status: <strong>{result.membershipStatus}</strong>.
              </p>
              {membershipContext && (
                <p>
                  You now have access to <strong>{membershipContext.orgName}</strong>. Review the available sign-in
                  options below and continue when you are ready.
                </p>
              )}
              <p>
                Email verification handle expires:{' '}
                <strong>{formatDateTime(result.emailTxExpiresAt ?? null)}</strong>
              </p>
              {localConnections.length > 0 ? (
                <p>
                  Local methods enabled: <strong>{localConnectionLabels.join(', ')}</strong>.
                </p>
              ) : (
                <p>Local methods are currently unavailable for this invitation.</p>
              )}
              {requiresSso && (
                <p className="auth-note">
                  This organization requires SSO. Use one of the connections provided below or contact an administrator.
                </p>
              )}
              {hasSsoConnections && (
                <div>
                  <p>SSO connections:</p>
                  <ul>
                    {ssoConnections.map((connection) => (
                      <li key={`${connection.name}-${connection.type}`}>
                        {connection.name} ({connection.type.toUpperCase()})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!flowPrimed && (
                <p className="auth-note">
                  Continue to the sign-in page and follow the prompts to finish setting up your access.
                </p>
              )}
              <button type="button" className="auth-primary" onClick={goToNextStep}>
                Continue to sign-in options
              </button>
            </section>
          )}
        </div>
      </section>
    </main>
  );
};

export default AcceptInvitePage;
